import { useMemo } from 'react';

import type { Asset } from '@shared/types';

import { useDayCandles, type MinuteCandle } from './useDayCandles';

export interface PricePoint {
  timestamp: number;
  price: number;
}

export interface UseDayPriceHistoryProps {
  asset: Asset;
  realtimeData: PricePoint[]; // The existing by-second data from WebSocket
}

export interface UseDayPriceHistoryResult {
  dayPriceHistory: PricePoint[];
  isLoading: boolean;
  error: string | null;
  dayCandles: MinuteCandle[];
  realtimeStartTime: number | null;
}

/**
 * Hook that combines minute candle data (24 hours) with real-time by-second data
 * Creates a seamless price history for zooming from daily view down to second-level detail
 */
export function useDayPriceHistory({
  asset,
  realtimeData,
}: UseDayPriceHistoryProps): UseDayPriceHistoryResult {
  const { candles, isLoading, error } = useDayCandles(asset);

  const dayPriceHistory = useMemo(() => {
    if (candles.length === 0) {
      return realtimeData;
    }

    // Find where real-time data begins (usually last 15 minutes)
    const realtimeStartTime =
      realtimeData.length > 0 ? realtimeData[0].timestamp : null;

    // Convert minute candles to price points using close prices
    // Do NOT apply gap filling to minute candle data - keep it smooth
    const historicalPoints: PricePoint[] = candles
      .filter(candle => {
        // Only include candles that are before the real-time data starts
        // Add 30 second buffer to avoid overlap
        return (
          !realtimeStartTime || candle.timestamp < realtimeStartTime - 30000
        );
      })
      .map(candle => ({
        timestamp: candle.timestamp,
        price: candle.close, // Use close price for consistency
      }));

    // Combine historical minute data (smooth) with real-time second data
    const combined = [...historicalPoints, ...realtimeData];

    // Sort by timestamp to ensure proper ordering
    combined.sort((a, b) => a.timestamp - b.timestamp);

    // Remove any duplicate timestamps (keep the more recent/accurate data)
    const deduplicated: PricePoint[] = [];
    const seenTimestamps = new Set<number>();

    for (let i = combined.length - 1; i >= 0; i--) {
      const point = combined[i];
      const roundedTimestamp = Math.floor(point.timestamp / 1000) * 1000; // Round to second

      if (!seenTimestamps.has(roundedTimestamp)) {
        seenTimestamps.add(roundedTimestamp);
        deduplicated.unshift(point);
      }
    }

    return deduplicated;
  }, [candles, realtimeData]);

  const realtimeStartTime = useMemo(() => {
    return realtimeData.length > 0 ? realtimeData[0].timestamp : null;
  }, [realtimeData]);

  return {
    dayPriceHistory,
    isLoading,
    error,
    dayCandles: candles,
    realtimeStartTime,
  };
}
